import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell, Megaphone, Scale, FileText, Calendar, MessageCircle, Eye,
  CheckCheck, ArrowRight, Inbox, Filter,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  listNotifications, markRead, markAllRead, deleteNotification,
} from "../lib/notificationsApi";

const ICONS = {
  bell: Bell, megaphone: Megaphone, scale: Scale,
  document: FileText, calendar: Calendar, message: MessageCircle, eye: Eye,
};

function rel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("ar-IQ", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const data = await listNotifications({
      limit: 100,
      unread_only: filter === "unread",
    });
    setItems(data.items || []);
    setUnread(data.unread_count || 0);
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);  // eslint-disable-line

  const handleRead = async (id) => {
    setItems((p) => p.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
    await markRead(id);
    setUnread((u) => Math.max(0, u - 1));
  };
  const handleAllRead = async () => {
    setItems((p) => p.map((x) => ({ ...x, is_read: true })));
    setUnread(0);
    await markAllRead();
  };
  const handleDel = async (id) => {
    setItems((p) => p.filter((x) => x.id !== id));
    await deleteNotification(id);
  };

  return (
    <div className="min-h-screen bg-slate-50/40">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Bell className="text-blue-600" size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">الإشعارات</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {unread > 0 ? `${unread} إشعار غير مقروء` : "كل الإشعارات مقروءة"}
              </p>
            </div>
          </div>
          {unread > 0 && (
            <Button
              data-testid="page-mark-all-read"
              variant="outline"
              size="sm"
              onClick={handleAllRead}
              className="gap-2"
            >
              <CheckCheck size={16} /> تعليم الكل كمقروء
            </Button>
          )}
        </div>

        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="mb-4">
            <TabsTrigger value="all" data-testid="filter-all">الكل</TabsTrigger>
            <TabsTrigger value="unread" data-testid="filter-unread">
              غير مقروءة {unread > 0 && <Badge variant="destructive" className="mr-1">{unread}</Badge>}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="py-20 text-center text-slate-400">جاري التحميل…</div>
        ) : items.length === 0 ? (
          <div className="bg-white border rounded-2xl py-20 text-center">
            <Inbox className="mx-auto text-slate-300 mb-3" size={48} />
            <h3 className="text-slate-700 font-semibold">لا توجد إشعارات</h3>
            <p className="text-sm text-slate-500 mt-1">
              ستظهر هنا التحديثات والتنبيهات المتعلقة بنشاطك
            </p>
          </div>
        ) : (
          <div className="bg-white border rounded-2xl divide-y divide-slate-100 overflow-hidden">
            {items.map((n) => {
              const Icon = ICONS[n.icon] || Bell;
              const accent = n.priority === "urgent" ? "bg-red-50/40" : !n.is_read ? "bg-blue-50/30" : "";
              const content = (
                <div className={`flex gap-3 py-4 px-4 hover:bg-slate-50 transition ${accent}`}>
                  <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    n.priority === "urgent" ? "bg-red-100 text-red-600" :
                    !n.is_read ? "bg-blue-100 text-blue-600" :
                    "bg-slate-100 text-slate-500"
                  }`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className={`text-sm leading-tight ${!n.is_read ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`}>
                        {n.title}
                      </h3>
                      <span className="shrink-0 text-[11px] text-slate-400">{rel(n.created_at)}</span>
                    </div>
                    <p className="mt-1 text-[13px] text-slate-600 leading-relaxed">{n.body}</p>
                    <div className="mt-2 flex items-center gap-3">
                      {!n.is_read && (
                        <button
                          onClick={(e) => { e.preventDefault(); handleRead(n.id); }}
                          className="text-xs text-blue-600 font-medium hover:underline"
                        >
                          تعليم كمقروء
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.preventDefault(); handleDel(n.id); }}
                        className="text-xs text-red-500 font-medium hover:underline"
                      >
                        حذف
                      </button>
                      {n.link && (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          فتح <ArrowRight size={12} />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
              return n.link ? (
                <Link key={n.id} to={n.link} onClick={() => !n.is_read && handleRead(n.id)} className="block">{content}</Link>
              ) : (
                <div key={n.id}>{content}</div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
